// The interior of a tile (PRD sections 6, 14.3 and 19.6): the tile's block with its checkered grid of
// cells, a rim around it with the doors and a few trees, and on the grid the machines and belts the
// player built, the items riding the belts and the ghost of what is being placed. Static parts are
// merged into a couple of meshes, the layout into one more, and items and ghosts are instanced, so the
// whole interior costs a handful of draw calls. It only describes the scene; the stage draws it.
import {
  BoxGeometry,
  BufferAttribute,
  BufferGeometry,
  Color,
  CylinderGeometry,
  DirectionalLight,
  DodecahedronGeometry,
  DoubleSide,
  EdgesGeometry,
  Group,
  HemisphereLight,
  IcosahedronGeometry,
  InstancedMesh,
  LineBasicMaterial,
  LineSegments,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Object3D,
  PerspectiveCamera,
  PlaneGeometry,
  Scene,
  Shape,
  ShapeGeometry,
  Vector3,
  ConeGeometry,
  type Material,
} from 'three';
import type { BuildGhost } from '../interior/ghost';
import type { ItemPose } from '../interior/belt-tracks';
import type { GameData } from '../sim/data';
import type { Cell, Side } from '../sim/geometry';
import type { TileReport } from '../sim/report';
import { createBatchMaterial, GeometryBatch } from './batch';
import { placeCamera, type CameraFraming, type CameraMode, type Fit, type Pose, type Rect, type Viewport } from './camera-rig';
import { beltPiece, chevronGeometry, doorBelt, itemGeometry, machinePiece, SIDE_VECTOR, sideAngle } from './interior-prefabs';
import { C } from './palette';
import { box, gate } from './prefabs';
import { besideDoor } from '../ui/label-layout';

type InteriorReport = NonNullable<TileReport['interior']>;

/** Width of the rim around the grid, where the doors and the trees stand, in cells. */
export const RIM = 0.5;
/** Sea level below the interior's block. */
const SEA_Y = -0.62;
const GRASS_LAYER = 0.3;
/** Most items of one kind drawn at once: 64 belts with two items each, and some room. */
const ITEM_CAPACITY = 192;
/** Most ghost cells: a belt across the whole grid and back. */
const GHOST_CAPACITY = 128;

const INTERIOR_ANGLES: Readonly<Record<CameraMode, { readonly yaw: number; readonly pitch: number }>> = {
  // The map's turn, and nearly top-down (about 70 degrees) so cells are easy to touch (PRD section 14.2).
  tilted: { yaw: 0.62, pitch: 1.22 },
  top: { yaw: 0, pitch: Math.PI / 2 - 0.01 },
};
/** Room kept free between the block, the screen's edges and the HUD, in CSS pixels. */
const FIT_MARGIN = 8;
/** Room a door's figure needs, in CSS pixels (a little more than «40/min» with its icon). */
const LABEL_WIDTH = 140;
const LABEL_HEIGHT = 36;
/** Height of the top of a door's gate, where its figure is pinned. */
const DOOR_LABEL_Y = 1.5;
/** Shifts tried to slide the block between the HUD panels, in CSS pixels. */
const SHIFTS = Array.from({ length: 25 }, (_, i) => (i - 12) * 16);

type Point2 = readonly [number, number];

/** Convex hull of screen points, counter-clockwise (Andrew's monotone chain). */
function hull(points: Point2[]): Point2[] {
  const p = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const cross = (o: Point2, a: Point2, b: Point2): number => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower: Point2[] = [];
  for (const q of p) {
    while (lower.length >= 2 && cross(lower[lower.length - 2] as Point2, lower[lower.length - 1] as Point2, q) <= 0) lower.pop();
    lower.push(q);
  }
  const upper: Point2[] = [];
  for (const q of p.reverse()) {
    while (upper.length >= 2 && cross(upper[upper.length - 2] as Point2, upper[upper.length - 1] as Point2, q) <= 0) upper.pop();
    upper.push(q);
  }
  return [...lower.slice(0, -1), ...upper.slice(0, -1)];
}

/** True if a convex polygon and a rectangle overlap (separating axis test). */
export function polygonHitsRect(poly: readonly Point2[], r: Rect): boolean {
  const corners: Point2[] = [
    [r.left, r.top],
    [r.right, r.top],
    [r.right, r.bottom],
    [r.left, r.bottom],
  ];
  const axes: Point2[] = [
    [1, 0],
    [0, 1],
  ];
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i] as Point2;
    const b = poly[(i + 1) % poly.length] as Point2;
    axes.push([b[1] - a[1], a[0] - b[0]]);
  }
  return axes.every(([ax, ay]) => {
    const span = (pts: readonly Point2[]): [number, number] => {
      let min = Infinity;
      let max = -Infinity;
      for (const [x, y] of pts) {
        const d = x * ax + y * ay;
        min = Math.min(min, d);
        max = Math.max(max, d);
      }
      return [min, max];
    };
    const [p0, p1] = span(poly);
    const [r0, r1] = span(corners);
    return p1 > r0 && r1 > p0;
  });
}

/**
 * The interior's framing: the whole block as large as it fits on screen, clear of the HUD panels.
 * It projects the block's corners and slides the frame up or down between the panels, so it holds for
 * any screen shape, either camera mode, and wherever the HUD puts its buttons.
 */
export function interiorFraming(size: Cell, labels: readonly DoorAnchor[] = []): CameraFraming {
  const hx = size[0] / 2 + RIM;
  const hz = size[1] / 2 + RIM;
  const corners: Vector3[] = [];
  for (const x of [-hx, hx]) for (const z of [-hz, hz]) corners.push(new Vector3(x, 0, z), new Vector3(x, -GRASS_LAYER, z));
  const scratch = new PerspectiveCamera(32, 1, 0.1, 400);
  const point = new Vector3();
  const toScreen = (v: Vector3, width: number, height: number): Point2 => {
    point.copy(v).project(scratch);
    return [((point.x + 1) / 2) * width, ((1 - point.y) / 2) * height];
  };
  const cache = new Map<string, Fit>();
  const fits = (pose: Pose, view: Viewport): boolean => {
    placeCamera(scratch, pose, view);
    const { width, height } = view;
    const projected: Point2[] = [];
    for (const c of corners) {
      point.copy(c).project(scratch);
      const x = ((point.x + 1) / 2) * width;
      const y = ((1 - point.y) / 2) * height;
      if (x < FIT_MARGIN || x > width - FIT_MARGIN || y < FIT_MARGIN || y > height - FIT_MARGIN) return false;
      projected.push([x, y]);
    }
    // The doors' figures float beside the block: they stay whole on screen and clear of the HUD too.
    const boxes: Rect[] = [];
    for (const l of labels) {
      const [ax, ay] = toScreen(l.at, width, height);
      const [ox, oy] = toScreen(l.out, width, height);
      const { left, top } = besideDoor({ x: ax, y: ay }, { x: ox, y: oy }, LABEL_WIDTH, LABEL_HEIGHT);
      if (left < FIT_MARGIN || top < FIT_MARGIN || left + LABEL_WIDTH > width - FIT_MARGIN || top + LABEL_HEIGHT > height - FIT_MARGIN) return false;
      boxes.push({ left, top, right: left + LABEL_WIDTH, bottom: top + LABEL_HEIGHT });
    }
    const outline = hull(projected);
    return view.obstacles.every((o) => {
      const room = { left: o.left - FIT_MARGIN, top: o.top - FIT_MARGIN, right: o.right + FIT_MARGIN, bottom: o.bottom + FIT_MARGIN };
      return !polygonHitsRect(outline, room) && boxes.every((b) => b.right <= room.left || b.left >= room.right || b.bottom <= room.top || b.top >= room.bottom);
    });
  };
  return {
    fov: 32,
    angles: INTERIOR_ANGLES,
    targets: { tilted: { x: 0, z: 0 }, top: { x: 0, z: 0 } },
    targetY: 0,
    fit: (mode, view) => {
      const key = `${mode}:${view.width}:${view.height}:${view.obstacles.map((o) => `${o.left},${o.top},${o.right},${o.bottom}`).join(';')}`;
      const known = cache.get(key);
      if (known) return known;
      const angles = INTERIOR_ANGLES[mode];
      let best: Fit = { distance: Infinity, shiftY: 0 };
      for (const shiftY of SHIFTS) {
        let lo = 1;
        let hi = 400;
        if (!fits({ x: 0, y: 0, z: 0, distance: hi, shiftY, ...angles }, view)) continue;
        for (let i = 0; i < 32; i++) {
          const mid = (lo + hi) / 2;
          if (fits({ x: 0, y: 0, z: 0, distance: mid, shiftY, ...angles }, view)) hi = mid;
          else lo = mid;
        }
        if (hi < best.distance - 1e-3 || (Math.abs(hi - best.distance) <= 1e-3 && Math.abs(shiftY) < Math.abs(best.shiftY))) best = { distance: hi, shiftY };
      }
      // A screen too small for any clear frame still shows the whole block, centred.
      if (!Number.isFinite(best.distance)) best = { distance: 40, shiftY: 0 };
      cache.set(key, best);
      return best;
    },
    bounds: { minX: -size[0] / 2, maxX: size[0] / 2, minZ: -size[1] / 2, maxZ: size[1] / 2 },
    margin: 0,
    // Pinching out past the widest view goes back to the island (PRD section 19.4).
    zoom: { min: 0.45, max: 1.15 },
  };
}

/** A door's figure goes beside `at` (the top of the gate), on the side towards `out` (further out of the tile). */
export interface DoorAnchor {
  readonly at: Vector3;
  readonly out: Vector3;
}

/** A point on the interior in cell units: (0, 0) is the outer corner of the first cell. */
export interface CellPoint {
  readonly x: number;
  readonly y: number;
}

export interface InteriorCounts {
  readonly machines: number;
  readonly belts: number;
  readonly items: number;
  readonly decor: number;
}

const GHOST_OK = new Color('#5fd08a');
const GHOST_WARN = new Color('#f5b25a');

export class InteriorScene {
  readonly scene = new Scene();
  readonly sun = new DirectionalLight(C.sun, 3.1);
  readonly size: Cell;
  readonly framing: CameraFraming;
  /** Where each door's figure floats, by port id. */
  readonly doorAnchors = new Map<string, DoorAnchor>();
  /** Where each machine's state floats, by machine id; refreshed with the layout. */
  readonly machineAnchors = new Map<string, Vector3>();

  private readonly data: GameData;
  private readonly batchMaterial = createBatchMaterial();
  private readonly materials: Material[] = [this.batchMaterial];
  private layoutMesh: Mesh | undefined;
  private layoutKey = '';
  private readonly itemMeshes = new Map<string, InstancedMesh>();
  private readonly ghostCells: InstancedMesh;
  private readonly ghostEdges: InstancedMesh;
  private readonly ghostArrows: InstancedMesh;
  private readonly ghostMachine = new Group();
  private ghostMachineKey = '';
  private readonly ghostMaterials: { fill: MeshBasicMaterial; edge: MeshBasicMaterial; line: LineBasicMaterial };
  private readonly scratch = { matrix: new Matrix4(), object: new Object3D() };
  private decorCount = 0;
  private counts: InteriorCounts = { machines: 0, belts: 0, items: 0, decor: 0 };

  constructor(tile: TileReport, data: GameData, opts: { readonly shadows?: boolean } = {}) {
    const interior = tile.interior;
    if (!interior) throw new Error(`${tile.name} no tiene interior`);
    this.data = data;
    this.size = interior.size;
    this.scene.background = new Color(C.sky);
    this.scene.add(new HemisphereLight(C.skyLight, C.groundLight, 1.05));
    this.setupSun();
    this.buildSea();
    this.buildBlock(tile);
    this.framing = interiorFraming(this.size, [...this.doorAnchors.values()]);

    // Ghosts draw over everything: one that does not fit, on top of a machine, must still be seen.
    const fill = this.material(new MeshBasicMaterial({ color: '#ffffff', transparent: true, opacity: 0.55, depthWrite: false, depthTest: false }));
    const edge = this.material(new MeshBasicMaterial({ color: '#ffffff', transparent: true, opacity: 0.7, depthWrite: false, depthTest: false }));
    const line = this.material(new LineBasicMaterial({ color: '#ffffff', transparent: true, opacity: 0.9, depthTest: false }));
    this.ghostMaterials = { fill, edge, line };
    this.ghostEdges = this.instanced(new BoxGeometry(0.96, 0.03, 0.96), edge, GHOST_CAPACITY, 'fantasma-borde');
    this.ghostCells = this.instanced(new BoxGeometry(0.84, 0.05, 0.84), fill, GHOST_CAPACITY, 'fantasma');
    // Transparent (though solid) so they are drawn after the fill, in the same pass, on top of it.
    this.ghostArrows = this.instanced(chevronGeometry(1.6), this.material(new MeshBasicMaterial({ color: '#ffffff', transparent: true, depthTest: false })), GHOST_CAPACITY, 'fantasma-flechas');
    for (const mesh of [this.ghostEdges, this.ghostCells]) mesh.renderOrder = 4;
    this.ghostArrows.renderOrder = 5;
    this.ghostMachine.name = 'fantasma-maquina';
    this.ghostMachine.visible = false;
    this.scene.add(this.ghostMachine);
    this.setShadows(opts.shadows ?? true);
  }

  get objectCounts(): InteriorCounts {
    return this.counts;
  }

  setShadows(on: boolean): void {
    this.sun.castShadow = on;
  }

  /** World position of a point in cell units, on the ground. */
  toWorld(x: number, y: number, height = 0): Vector3 {
    return new Vector3(x - this.size[0] / 2, height, y - this.size[1] / 2);
  }

  /** Cell units of a point on the ground. */
  toCell(p: Vector3): CellPoint {
    return { x: p.x + this.size[0] / 2, y: p.z + this.size[1] / 2 };
  }

  /** The cell a point in cell units falls on, or nothing off the grid. */
  cellAt(p: CellPoint): Cell | undefined {
    const c: Cell = [Math.floor(p.x), Math.floor(p.y)];
    return c[0] >= 0 && c[1] >= 0 && c[0] < this.size[0] && c[1] < this.size[1] ? c : undefined;
  }

  /** Rebuilds the machines and belts when the layout changed. Returns true if it did (shadows need redrawing). */
  setLayout(interior: InteriorReport): boolean {
    const key = [
      ...interior.machines.map((m) => `${m.id}:${m.origin.join(',')}:${m.rotation}`),
      ...interior.belts.map((b) => `${b.key}:${b.exit}:${b.inputs.join('')}`),
    ].join('|');
    if (key === this.layoutKey) return false;
    this.layoutKey = key;
    if (this.layoutMesh) {
      this.scene.remove(this.layoutMesh);
      this.layoutMesh.geometry.dispose();
      this.layoutMesh = undefined;
    }
    const b = new GeometryBatch();
    for (const belt of interior.belts) {
      const p = this.toWorld(belt.cell[0] + 0.5, belt.cell[1] + 0.5);
      // A belt nothing feeds yet still shows where it starts: straight behind its exit.
      const inputs: Side[] = belt.inputs.length > 0 ? [...belt.inputs] : [({ N: 'S', S: 'N', E: 'W', W: 'E' } as const)[belt.exit]];
      beltPiece(b, p.x, p.z, belt.exit, inputs);
    }
    this.machineAnchors.clear();
    for (const m of interior.machines) {
      const def = this.data.machines.get(m.type);
      if (!def) continue;
      const xs = m.cells.map((c) => c[0]);
      const ys = m.cells.map((c) => c[1]);
      const cx = (Math.min(...xs) + Math.max(...xs) + 1) / 2;
      const cy = (Math.min(...ys) + Math.max(...ys) + 1) / 2;
      const p = this.toWorld(cx, cy);
      b.group({ position: [p.x, 0, p.z], rotation: [0, (-m.rotation * Math.PI) / 2, 0] }, () => {
        machinePiece(b, def);
      });
      this.machineAnchors.set(m.id, this.toWorld(cx, cy, 1.45));
    }
    if (interior.belts.length + interior.machines.length > 0) {
      const mesh = new Mesh(b.build(), this.batchMaterial);
      mesh.name = 'distribucion';
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.matrixAutoUpdate = false;
      this.scene.add(mesh);
      this.layoutMesh = mesh;
    }
    this.counts = { ...this.counts, machines: interior.machines.length, belts: interior.belts.length };
    return true;
  }

  /** Puts the items riding the belts where the flow says they are. */
  setItems(poses: readonly ItemPose[]): void {
    const used = new Map<string, number>();
    const { matrix, object } = this.scratch;
    for (const pose of poses) {
      const mesh = this.itemMesh(pose.item);
      const i = used.get(pose.item) ?? 0;
      if (i >= ITEM_CAPACITY) continue;
      used.set(pose.item, i + 1);
      const p = this.toWorld(pose.x, pose.y, 0.115);
      object.position.copy(p);
      object.rotation.set(0, -pose.heading, 0);
      object.updateMatrix();
      mesh.setMatrixAt(i, matrix.copy(object.matrix));
    }
    let total = 0;
    for (const [item, mesh] of this.itemMeshes) {
      const n = used.get(item) ?? 0;
      total += n;
      mesh.count = n;
      mesh.visible = n > 0;
      mesh.instanceMatrix.needsUpdate = true;
    }
    this.counts = { ...this.counts, items: total };
  }

  /** Shows the ghost of what is being placed (green if it fits, amber if not), or hides it. */
  setGhost(ghost: BuildGhost | undefined, pulse = 0): void {
    const fill = this.ghostMaterials.fill;
    fill.opacity = 0.5 + 0.12 * pulse;
    const { matrix, object } = this.scratch;
    const color = ghost?.fits === false ? GHOST_WARN : GHOST_OK;
    if (!ghost || ghost.kind !== 'belt') {
      this.ghostCells.count = 0;
      this.ghostEdges.count = 0;
      this.ghostArrows.count = 0;
    } else {
      const n = Math.min(GHOST_CAPACITY, ghost.cells.length);
      for (let i = 0; i < n; i++) {
        const [c, r] = ghost.cells[i] as Cell;
        const p = this.toWorld(c + 0.5, r + 0.5, 0.06);
        object.position.copy(p);
        object.rotation.set(0, 0, 0);
        object.scale.set(1, 1, 1);
        object.updateMatrix();
        this.ghostCells.setMatrixAt(i, matrix.copy(object.matrix));
        this.ghostCells.setColorAt(i, color);
        this.ghostEdges.setMatrixAt(i, matrix);
        object.position.y = 0.1;
        object.rotation.set(0, sideAngle(ghost.sides[i] ?? 'E'), 0);
        object.updateMatrix();
        this.ghostArrows.setMatrixAt(i, matrix.copy(object.matrix));
      }
      this.ghostCells.count = n;
      this.ghostEdges.count = n;
      this.ghostArrows.count = n;
      for (const mesh of [this.ghostCells, this.ghostEdges, this.ghostArrows]) {
        mesh.instanceMatrix.needsUpdate = true;
        if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
        mesh.computeBoundingSphere();
      }
    }
    if (ghost?.kind !== 'machine') {
      this.ghostMachine.visible = false;
      return;
    }
    const def = this.data.machines.get(ghost.machine);
    if (!def) return;
    const key = def.id;
    if (key !== this.ghostMachineKey) {
      this.ghostMachineKey = key;
      this.buildMachineGhost(def.size, def.in, def.out);
    }
    const xs = ghost.cells.map((c) => c[0]);
    const ys = ghost.cells.map((c) => c[1]);
    const p = this.toWorld((Math.min(...xs) + Math.max(...xs) + 1) / 2, (Math.min(...ys) + Math.max(...ys) + 1) / 2);
    this.ghostMachine.position.set(p.x, 0, p.z);
    this.ghostMachine.rotation.set(0, (-ghost.rotation * Math.PI) / 2, 0);
    for (const child of this.ghostMachine.children) {
      if (child instanceof Mesh && child.name === 'relleno') (child.material as MeshBasicMaterial).color.copy(color);
    }
    this.ghostMachine.visible = true;
  }

  dispose(): void {
    this.scene.traverse((o) => {
      if (o instanceof Mesh || o instanceof LineSegments) (o.geometry as BufferGeometry).dispose();
      if (o instanceof InstancedMesh) o.dispose();
    });
    for (const m of this.materials) m.dispose();
  }

  // ------------------------------------------------------------ setup ---

  private material<T extends Material>(m: T): T {
    this.materials.push(m);
    return m;
  }

  private instanced(geometry: BufferGeometry, material: Material, count: number, name: string): InstancedMesh {
    const mesh = new InstancedMesh(geometry, material, count);
    mesh.name = name;
    mesh.count = 0;
    mesh.frustumCulled = false;
    this.scene.add(mesh);
    return mesh;
  }

  private itemMesh(item: string): InstancedMesh {
    const known = this.itemMeshes.get(item);
    if (known) return known;
    const color = this.data.items.get(item)?.color ?? '#ffffff';
    const mesh = new InstancedMesh(itemGeometry(item, color), this.batchMaterial, ITEM_CAPACITY);
    mesh.name = `objeto-${item}`;
    mesh.count = 0;
    mesh.receiveShadow = true;
    // Items move every frame: they never cast shadows, so the shadow map stays still.
    mesh.castShadow = false;
    mesh.frustumCulled = false;
    this.itemMeshes.set(item, mesh);
    this.scene.add(mesh);
    return mesh;
  }

  private buildMachineGhost(size: Cell, input: { cell: Cell; side: Side }, output: { cell: Cell; side: Side }): void {
    for (const child of [...this.ghostMachine.children]) {
      this.ghostMachine.remove(child);
      if (child instanceof Mesh || child instanceof LineSegments) (child.geometry as BufferGeometry).dispose();
      if (child instanceof Mesh && child.name === 'relleno') (child.material as Material).dispose();
    }
    const [w, d] = size;
    const shape = new BoxGeometry(w - 0.1, 0.5, d - 0.1);
    const fill = new Mesh(shape, new MeshBasicMaterial({ color: GHOST_OK, transparent: true, opacity: 0.55, depthWrite: false, depthTest: false }));
    fill.name = 'relleno';
    fill.position.y = 0.27;
    fill.renderOrder = 4;
    const lines = new LineSegments(new EdgesGeometry(shape), this.ghostMaterials.line);
    lines.position.y = 0.27;
    lines.renderOrder = 5;
    this.ghostMachine.add(fill, lines);
    const cellCentre = ([c, r]: Cell): [number, number] => [c + 0.5 - w / 2, r + 0.5 - d / 2];
    for (const [socket, kind] of [
      [input, 'in'],
      [output, 'out'],
    ] as const) {
      const [x, z] = cellCentre(socket.cell);
      const [vx, vz] = SIDE_VECTOR[socket.side];
      const towards: Side = kind === 'in' ? ({ N: 'S', S: 'N', E: 'W', W: 'E' } as const)[socket.side] : socket.side;
      const arrow = new Mesh(chevronGeometry(2), this.material(new MeshBasicMaterial({ color: kind === 'in' ? C.portIn : C.portOut, transparent: true, depthTest: false })));
      arrow.position.set(x + vx * 0.62, 0.55, z + vz * 0.62);
      arrow.rotation.set(0, sideAngle(towards), 0);
      arrow.renderOrder = 6;
      this.ghostMachine.add(arrow);
    }
  }

  private setupSun(): void {
    // Sun from the upper left, as on the map: shadows fall to the lower right.
    this.sun.position.set(-5, 11, -3);
    this.sun.target.position.set(0, 0, 0);
    this.sun.shadow.mapSize.set(2048, 2048);
    this.sun.shadow.bias = -0.0006;
    this.sun.shadow.normalBias = 0.02;
    this.sun.shadow.radius = 3;
    const cam = this.sun.shadow.camera;
    const r = Math.max(this.size[0], this.size[1]) / 2 + RIM + 1.5;
    cam.left = -r;
    cam.right = r;
    cam.top = r;
    cam.bottom = -r;
    cam.near = 1;
    cam.far = 30;
    cam.updateProjectionMatrix();
    this.scene.add(this.sun, this.sun.target);
  }

  private addStatic(mesh: Mesh, name: string): Mesh {
    mesh.name = name;
    mesh.updateMatrix();
    mesh.matrixAutoUpdate = false;
    this.scene.add(mesh);
    return mesh;
  }

  private buildSea(): void {
    const size = 120;
    const deep = new Mesh(new PlaneGeometry(size, size), this.material(new MeshBasicMaterial({ color: C.seaDeep })));
    deep.rotation.x = -Math.PI / 2;
    deep.position.y = SEA_Y - 5;
    this.addStatic(deep, 'fondo');
    const surface = new Mesh(new PlaneGeometry(size, size), this.material(new MeshStandardMaterial({ color: C.sea, transparent: true, opacity: 0.38, roughness: 0.4, depthWrite: false })));
    surface.rotation.x = -Math.PI / 2;
    surface.position.y = SEA_Y;
    surface.receiveShadow = true;
    surface.renderOrder = 2;
    this.addStatic(surface, 'mar');
  }

  /** The block: checkered cells, the rim, the tufted grass edge, the sand layer, foam and the fading column. */
  private buildBlock(tile: TileReport): void {
    const [w, h] = this.size;
    const solid = new GeometryBatch();
    const flat = new GeometryBatch();
    for (let r = 0; r < h; r++) {
      for (let c = 0; c < w; c++) {
        const p = this.toWorld(c + 0.5, r + 0.5);
        solid.add(box(1, GRASS_LAYER, 1), C.grass[(c + r) % 2] as string, { position: [p.x, -GRASS_LAYER / 2, p.z] });
      }
    }
    // The rim: plain grass, a shade apart from the grid, so it reads as part of the tile but not as cells.
    const hx = w / 2 + RIM / 2;
    const hz = h / 2 + RIM / 2;
    for (const s of [-1, 1]) {
      solid.add(box(w + 2 * RIM, GRASS_LAYER, RIM), C.rim, { position: [0, -GRASS_LAYER / 2, s * hz] });
      solid.add(box(RIM, GRASS_LAYER, h), C.rim, { position: [s * hx, -GRASS_LAYER / 2, 0] });
    }
    const W = w + 2 * RIM;
    const H = h + 2 * RIM;
    const sandHeight = -GRASS_LAYER - SEA_Y;
    flat.add(box(W - 0.02, sandHeight, H - 0.02), C.sandLayer, { position: [0, SEA_Y + sandHeight / 2, 0] });
    for (const [side, len] of [
      ['N', W],
      ['S', W],
      ['E', H],
      ['W', H],
    ] as const) {
      const [vx, vz] = SIDE_VECTOR[side];
      flat.add(skirt(len), C.skirt, { position: [vx * (W / 2 + 0.005), -GRASS_LAYER + 0.01, vz * (H / 2 + 0.005)], rotation: [0, Math.atan2(vx, vz), 0] });
      flat.add(box(vx ? 0.05 : W + 0.06, 0.03, vz ? 0.05 : H + 0.06), C.foam, { position: [vx * (W / 2 + 0.02), SEA_Y + 0.01, vz * (H / 2 + 0.02)] });
    }
    this.buildDoors(tile, solid);
    this.buildDecor(solid);
    this.addBatch(solid, true, 'casilla');
    this.addBatch(flat, false, 'bordes');
    this.buildColumn(W, H);
  }

  private buildDoors(tile: TileReport, b: GeometryBatch): void {
    for (const port of tile.ports) {
      const edge = port.edge;
      if (!edge) continue;
      const [vx, vz] = SIDE_VECTOR[port.side];
      const centre = this.toWorld(edge.cell[0] + 0.5, edge.cell[1] + 0.5);
      const x = centre.x + vx * (0.5 + RIM / 2);
      const z = centre.z + vz * (0.5 + RIM / 2);
      doorBelt(b, x, z, port.side, RIM + 0.02, port.kind === 'in');
      gate(b, x + vx * 0.06, 0, z + vz * 0.06, [vx, vz], port.kind, 3.6);
      const at = new Vector3(x + vx * 0.2, DOOR_LABEL_Y, z + vz * 0.2);
      this.doorAnchors.set(port.id, { at, out: at.clone().add(new Vector3(vx, 0, vz)) });
    }
  }

  /** A few trees, rocks and red tufts on the rim, clear of the doors. */
  private buildDecor(b: GeometryBatch): void {
    const [w, h] = this.size;
    const ex = w / 2 + RIM / 2;
    const ez = h / 2 + RIM / 2;
    const trees: [number, number, string, number][] = [
      [-ex, -ez, C.autumn[0], 1],
      [ex, -ez, C.autumn[2], 0.9],
      [ex, ez, C.autumn[1], 1.05],
      [-ex, ez - 2.2, C.autumn[3], 0.8],
      [ex - 3.1, -ez, C.green[0], 0.85],
    ];
    for (const [x, z, color, s] of trees) {
      b.add(new CylinderGeometry(0.05 * s, 0.08 * s, 0.6 * s, 5), C.trunk, { position: [x, 0.3 * s, z] });
      b.add(new IcosahedronGeometry(0.36 * s, 0), color, { position: [x, 0.95 * s, z], rotation: [0, x * 3, 0], scale: [1, 1.55, 1] });
    }
    for (const [x, z, s] of [
      [-ex, ez, 0.16],
      [ex - 1.6, ez, 0.12],
      [-ex + 2.4, -ez, 0.11],
    ] as const) {
      b.add(new DodecahedronGeometry(s, 0), C.boulder, { position: [x, s * 0.6, z], rotation: [0.3, x, 0.2] });
    }
    for (const [x, z] of [
      [-ex + 0.9, ez],
      [ex, -ez + 2.6],
      [-ex + 4.2, ez],
    ] as const) {
      for (const k of [-0.08, 0.08]) b.add(new ConeGeometry(0.03, 0.16, 3), C.tuft, { position: [x + k, 0.08, z + k * 0.6] });
    }
    this.decorCount = trees.length + 6;
    this.counts = { ...this.counts, decor: this.decorCount };
  }

  private addBatch(batch: GeometryBatch, cast: boolean, name: string): void {
    const mesh = new Mesh(batch.build(), this.batchMaterial);
    mesh.castShadow = cast;
    mesh.receiveShadow = true;
    this.addStatic(mesh, name);
  }

  /** The pale translucent column under the block, fading towards the sea floor. */
  private buildColumn(W: number, H: number): void {
    const depth = 4;
    const pos: number[] = [];
    const col: number[] = [];
    const idx: number[] = [];
    const quads: [number, number, number, number][] = [
      [-W / 2, -H / 2, W / 2, -H / 2],
      [W / 2, -H / 2, W / 2, H / 2],
      [W / 2, H / 2, -W / 2, H / 2],
      [-W / 2, H / 2, -W / 2, -H / 2],
    ];
    quads.forEach(([x1, z1, x2, z2], i) => {
      const k = i * 4;
      pos.push(x1, SEA_Y, z1, x2, SEA_Y, z2, x1, SEA_Y - depth, z1, x2, SEA_Y - depth, z2);
      col.push(1, 1, 1, 0.85, 1, 1, 1, 0.85, 1, 1, 1, 0.05, 1, 1, 1, 0.05);
      idx.push(k, k + 2, k + 1, k + 1, k + 2, k + 3);
    });
    const geo = new BufferGeometry();
    geo.setAttribute('position', new BufferAttribute(new Float32Array(pos), 3));
    geo.setAttribute('color', new BufferAttribute(new Float32Array(col), 4));
    geo.setIndex(idx);
    const columns = new Mesh(geo, this.material(new MeshBasicMaterial({ color: C.column, vertexColors: true, transparent: true, side: DoubleSide, depthWrite: false })));
    columns.renderOrder = 1;
    this.addStatic(columns, 'columna');
  }
}

/** The tufted grass edge along one side of the block, as in the map's tiles but sized for the interior. */
function skirt(len: number): BufferGeometry {
  const s = new Shape();
  const n = Math.round(len / 0.24);
  s.moveTo(0, 0);
  for (let i = 0; i < n; i++) {
    s.lineTo(((i + 0.5) * len) / n, -0.12 - ((i * 37) % 5) * 0.025);
    s.lineTo(((i + 1) * len) / n, 0);
  }
  s.lineTo(len, 0.02);
  s.lineTo(0, 0.02);
  const geo = new ShapeGeometry(s);
  geo.translate(-len / 2, 0, 0);
  return geo;
}
